/**
 * PATH: packages/bills/src/components/captureValue.ts
 * FILE_ID: BILLS-PKG-CMP-CAPTUREVALUE-001
 * PURPOSE: Coerce a raw text snippet picked off the PDF into the shape the
 *          target bill field expects — money fields lose their currency
 *          symbols/thousands separators, date fields normalise to the
 *          yyyy-mm-dd a <input type=date> needs, everything else is a clean
 *          trimmed string.
 * WHY: Operator picks the correct value straight off the invoice ("select it
 *      from the pdf … it auto fills"). The PDF text is human-formatted
 *      ("$2,780.91", "17/06/2026") but the form inputs are typed, so a raw
 *      paste would land as "" in a date input or a NaN amount. This pure
 *      I→O function is the one place that mapping lives, so it's testable in
 *      isolation and reused by every consumer.
 *
 * FLOW: (fieldKey, rawText) → classify field → normalise → string
 *
 * DEPENDENCIES:
 *   - ./billFieldKeys: parseLineFieldKey (line.<idx>.<col> → column)
 * RELATED:
 *   - BillDataPanel.tsx: applies the result to the draft via setField.
 *   - PdfCanvasViewer.tsx: produces the raw picked text.
 */

import { parseLineFieldKey, type BillFieldKey } from './billFieldKeys';

const MONEY_FIELDS = new Set<string>([
  'subtotal',
  'gst',
  'total',
  'extra_charge_amount',
  // line-item numeric columns (post parse → column name)
  'unit_amount',
  'line_total',
]);

const DATE_FIELDS = new Set<string>(['invoice_date', 'due_date']);

/** Collapse runs of whitespace (incl. the newlines a multi-line drag picks
 *  up) into single spaces, then trim. */
export function normaliseWhitespace(raw: string): string {
  return (raw ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Pull the first number-like token out of human text and strip thousands
 * separators. "$2,780.91" → "2780.91"; "1,734.00 per week" → "1734.00";
 * "Qty 3" → "3". Returns '' when there's no number (caller keeps the field
 * empty rather than writing NaN).
 */
export function parseNumberLike(raw: string): string {
  const m = normaliseWhitespace(raw).match(/-?\d[\d,]*(?:\.\d+)?/);
  if (!m) return '';
  return m[0].replace(/,/g, '');
}

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function clampYear(y: number): number {
  // 2-digit years → 2000s (invoices are contemporary).
  return y < 100 ? 2000 + y : y;
}

/**
 * Normalise a human date to yyyy-mm-dd (what <input type=date> stores).
 * Handles dd/mm/yyyy, dd-mm-yyyy, "17 Jun 2026", "17 June 2026" and
 * already-ISO yyyy-mm-dd. Australian day-first convention (the tenant base).
 * Returns '' when it can't parse — the input then stays empty rather than
 * silently wrong.
 */
export function normaliseDate(raw: string): string {
  const text = normaliseWhitespace(raw);
  if (!text) return '';

  // Already ISO.
  const iso = text.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/);
  if (iso) {
    return `${iso[1]}-${pad2(Number(iso[2]))}-${pad2(Number(iso[3]))}`;
  }

  // dd/mm/yyyy or dd-mm-yyyy or dd.mm.yyyy (day-first).
  const dmy = text.match(/\b(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{2,4})\b/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = clampYear(Number(dmy[3]));
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      return `${year}-${pad2(month)}-${pad2(day)}`;
    }
  }

  // "17 Jun 2026" / "17 June 2026" / "Jun 17 2026".
  const named = text.match(/\b(\d{1,2})\s+([A-Za-z]{3,})\.?\s+(\d{2,4})\b/);
  if (named) {
    const day = Number(named[1]);
    const month = MONTHS[named[2].slice(0, 3).toLowerCase()];
    const year = clampYear(Number(named[3]));
    if (month && day >= 1 && day <= 31) return `${year}-${pad2(month)}-${pad2(day)}`;
  }
  const namedFirst = text.match(/\b([A-Za-z]{3,})\.?\s+(\d{1,2}),?\s+(\d{2,4})\b/);
  if (namedFirst) {
    const month = MONTHS[namedFirst[1].slice(0, 3).toLowerCase()];
    const day = Number(namedFirst[2]);
    const year = clampYear(Number(namedFirst[3]));
    if (month && day >= 1 && day <= 31) return `${year}-${pad2(month)}-${pad2(day)}`;
  }

  return '';
}

/**
 * f(fieldKey, rawPickedText) → normalised string for that field.
 * - money fields → bare number string ("2780.91")
 * - date fields  → "yyyy-mm-dd" (or '' if unparseable)
 * - everything else (incl. description) → whitespace-collapsed text
 */
export function coerceCapturedValue(fieldKey: BillFieldKey, raw: string): string {
  const line = parseLineFieldKey(fieldKey);
  const column = line ? line.column : fieldKey;

  if (column === 'quantity') return parseNumberLike(raw);
  if (MONEY_FIELDS.has(column)) return parseNumberLike(raw);
  if (DATE_FIELDS.has(column)) return normaliseDate(raw);
  return normaliseWhitespace(raw);
}
